import React, { useContext } from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

import { useSelector } from 'react-redux';
import { getNovoAIProperties, getSuggestedActivities } from 'redux/selectors/lecture-page';
import { getCourseAliases } from 'redux/selectors/course';
import { useAppDispatch } from 'redux/store';
import { setAddingSuggestedComponentSetId, setDestinationLecturePage, setNovoAIShowGenerateModal } from 'redux/actions/lecture-pages';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import { NovoAIItemType } from 'redux/schemas/models/lecture-page';

import { gray5 } from 'styles/global_defaults/colors';

import { Button } from 'react-bootstrap';
import { LecturePageMode } from 'lecture_pages/components';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { LoaderType } from 'shared/components/loading-placeholder';
import useAiItemMetadata from '../../hooks/use-ai-item-metadata';
import { AIComponentsContext } from '../../hooks/use-ai-components-generator';
import ActivityItem from '../suggest-activities/activity-item';
import AiActivityPreviewHeader from './ai-activity-preview-header';
import AiGeneratingLoader from './ai-generating-loader';

const AiGeneratedPreview = () => {
  const novoAIProperties = useSelector(getNovoAIProperties);
  const aliases = useSelector((state) => getCourseAliases(state));
  const activities = useSelector(getSuggestedActivities);
  const { isLoading } = useSelector(state => state.app.aiSuggestComponent);

  const { itemTextMapping, modalItemTypeIcons } = useAiItemMetadata();
  const { openCurrentLessonPreview } = useContext(AIComponentsContext);

  const params = useLecturePageParams();
  const dispatch = useAppDispatch();
  const { itemType } = novoAIProperties;

  const styles = css`
    & {
        padding-top: 35px;
    }
    .content {
      border: 1px solid ${gray5};
      border-width: 1px 0px;
    }
  `;

  const handleAddToLesson = (componentSetId: string) => {
    dispatch(setAddingSuggestedComponentSetId(componentSetId));
    dispatch(setDestinationLecturePage({
      mode: LecturePageMode.VIEW,
      lecturePageId: params.lecturePageId,
      catalogId: params.catalogId,
    }));
    openCurrentLessonPreview?.();
  };

  const showTitle = (type) => {
    switch (type) {
      case ComponentType.PROFILE_COMPLETION:
      case ComponentType.MEET_AND_GREET:
      case ComponentType.POLL:
      case ComponentType.EXERCISE:
      case ComponentType.PUBLIC_PEER_EVALUATION:
      case ComponentType.TOPIC:
      case ComponentType.QUIZ:
      case ComponentType.SURVEY:
        return true;
      default:
        return false;
    }
  };

  const closeModal = () => {
    dispatch(setNovoAIShowGenerateModal(false));
  };

  const showActivityList = itemType === NovoAIItemType.PUBLIC_FEEDBACK && activities.length > 1;

  return (
    <div css={styles} className='d-flex flex-column align-items-center'>
      <AiActivityPreviewHeader
        iconComponent={modalItemTypeIcons[itemType]}
        header={itemTextMapping[itemType]}
        className='mb-4'
      />
      <div className='content w-100'>
        {activities.length > 0 && activities.map((componentSet) => (
          <ActivityItem
            componentSet={componentSet}
            key={componentSet.id}
            showAddButton={showActivityList}
            showHeader={false}
            showTitle={showTitle(componentSet.activity.type)}
            onAddLesson={() => handleAddToLesson(componentSet.id)}
          />
        ))}
      </div>
      {isLoading && (
        <div className='mt-4'>
          <AiGeneratingLoader loaderType={LoaderType.HALF} />
        </div>
      )}

      {/* Button to close the modal if there are multiple activities or add the
      activity to the lesson if there is only one activity */}
      <Button
        className='mt-8 d-flex mb-4'
        onClick={showActivityList ? closeModal : () => handleAddToLesson(activities[0].id)}
      >
        {showActivityList
          ? t.NOVOED.CLOSE()
          : t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.ADD_TO_LESSON(aliases.lectureAliases)}
      </Button>
    </div>
  );
};

export default AiGeneratedPreview;

